import { useState, useRef, useEffect } from 'react'
import RecordingBox from '@/components/atoms/RecordingBox/RecordingBox'
import { Icon } from '@/icons'
import { button } from '@/components/atoms/Button/Button.css'
import { getStudentActivityTitle } from '@/utils/activityTitle';

interface StoryDialogProps {
  activity?: any;
  unitData?: any;
  onResponse?: (response: any) => void;
  existingResponse?: any;
  isHomework?: boolean;
}

interface SentenceItem {
  id: string;
  text: string;
  audioUrl: string | null;
  recorded: boolean;
  recordingBlob?: Blob;
  recordingUrl?: string;
}

const NEXT_AUDIO_DELAY_MS = 1000;

export default function StoryDialog({ activity, unitData, onResponse, existingResponse, isHomework = false }: StoryDialogProps) {
  // configJson에서 recordingEnabled 확인
  let config: any = {};
  try {
    if (activity?.configJson) {
      config = typeof activity.configJson === 'string' 
        ? JSON.parse(activity.configJson) 
        : activity.configJson;
    }
  } catch (e) {
    console.error('configJson 파싱 에러:', e);
  }
  
  const recordingEnabled = config.recordingEnabled === true;
  const title = getStudentActivityTitle(activity?.title, '스토리 & 다이얼로그');
  
  console.log('=== StoryDialog ===');
  console.log('Activity Items:', activity?.items);
  
  // activity.items에서 모든 문장 데이터 가져오기
  const items = activity?.items || [];
  const sentences: SentenceItem[] = items.map((item: any, index: number) => {
    let content: any = {};
    try {
      content = typeof item.contentJson === 'string' 
        ? JSON.parse(item.contentJson) 
        : item.contentJson;
    } catch (e) {
      console.error('contentJson 파싱 에러:', e);
    }
    
    console.log(`문장 ${index} 파싱:`, {
      sentence: content.sentence,
      audioUrl: content.audioUrl
    });
    
    return {
      id: `sentence-${index}`,
      text: content.sentence || content.text || content.content || '',
      audioUrl: content.audioUrl || content.audio || null,
      recorded: false
    };
  });
  
  console.log('Sentences:', sentences);
  
  // 전체 스크립트 합치기
  const fullScript = sentences.map(s => s.text).join(' ');
  
  // 각 문장별 녹음 상태 관리
  const [sentenceStates, setSentenceStates] = useState<Record<string, { recorded: boolean; blob?: Blob; url?: string }>>(
    existingResponse?.sentenceRecordings || {}
  );
  
  const hasAllRecordings = (recordings?: Record<string, { recorded: boolean }>) => {
    const sources = recordings || sentenceStates;
    return sentences.length === 0 || sentences.every((s) => sources?.[s.id]?.recorded);
  };

  const computeHomeworkReady = (overrides?: { recordings?: Record<string, { recorded: boolean }>; sentenceIndex?: number }) => {
    if (recordingEnabled) {
      return hasAllRecordings(overrides?.recordings);
    }
    const index = typeof overrides?.sentenceIndex === 'number' ? overrides.sentenceIndex : currentSentenceIndex;
    return sentences.length === 0 || index >= sentences.length - 1;
  };

  // 순차 재생 및 표시 관리
  const [hasStarted, setHasStarted] = useState(!!existingResponse?.hasStarted); // 듣기 시작 여부
  const [currentSentenceIndex, setCurrentSentenceIndex] = useState(existingResponse?.currentSentenceIndex || -1); // 현재 진행 중인 문장 인덱스 (-1: 시작 전)
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const audioRefs = useRef<(HTMLAudioElement | null)[]>([]);
  const playDelayTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // 특정 인덱스의 오디오 재생
  const playAudio = async (index: number) => {
    if (index >= sentences.length) {
      console.log('모든 문장 재생 완료');
      return;
    }
    
    const audio = audioRefs.current[index];
    const audioUrl = sentences[index].audioUrl;
    
    console.log(`문장 ${index} 재생 시도`);
    
    if (audio && audioUrl) {
      setIsPlayingAudio(true);
      
      try {
        audio.pause();
        audio.currentTime = 0;
        await audio.play();
        console.log(`문장 ${index} 재생 성공`);
      } catch (error) {
        console.error(`문장 ${index} 재생 실패:`, error);
        // 재생 실패 시 바로 문장 표시
        setCurrentSentenceIndex(index);
        setIsPlayingAudio(false);
      }
    } else {
      // 오디오가 없으면 바로 문장 표시
      console.log(`문장 ${index}에 오디오 없음, 바로 표시`);
      setCurrentSentenceIndex(index);
      setIsPlayingAudio(false);
    }
  };

  const schedulePlayAudio = (index: number, delayMs = NEXT_AUDIO_DELAY_MS) => {
    if (playDelayTimer.current) {
      clearTimeout(playDelayTimer.current);
    }
    playDelayTimer.current = setTimeout(() => {
      playAudio(index);
    }, delayMs);
  };

  // 메인 듣기 버튼 클릭 - 첫 문장 재생 시작
  const handleStartListening = async () => {
    if (hasStarted) {
      return;
    }
    
    console.log('스토리 듣기 시작 - 모든 오디오 unlock 후 첫 번째 문장 재생');
    
    const unlockPromises = audioRefs.current.map(async (audio, idx) => {
      if (audio && sentences[idx]?.audioUrl) {
        try {
          console.log(`오디오 ${idx} unlock 시도`);
          audio.volume = 0; // 소리 없이
          await audio.play();
          audio.pause();
          audio.currentTime = 0;
          audio.volume = 1; // 볼륨 복원
          console.log(`오디오 ${idx} unlock 완료`);
        } catch (error) {
          console.error(`오디오 ${idx} unlock 실패:`, error);
        }
      }
    });
    
    await Promise.all(unlockPromises);
    console.log('모든 오디오 unlock 완료');
    
    setHasStarted(true);
    console.log('첫 번째 문장 재생 예약');
    schedulePlayAudio(0, 0);
  };
  
  // 오디오 재생 완료 시 - 문장 표시
  const handleAudioEnded = (index: number) => {
    console.log(`문장 ${index} 재생 완료 - 문장 표시`);
    setCurrentSentenceIndex(index);
    setIsPlayingAudio(false);
    
    // 중간 과정은 저장하지 않음 (다음 버튼 클릭 시에만 저장)
    
    // 녹음이 비활성화된 경우, 자동으로 다음 문장 재생
    if (!recordingEnabled) {
      const nextIndex = index + 1;
      if (nextIndex < sentences.length) {
        console.log(`녹음 비활성화 - 다음 문장 ${nextIndex} 자동 재생 (지연 ${NEXT_AUDIO_DELAY_MS}ms)`);
        schedulePlayAudio(nextIndex, NEXT_AUDIO_DELAY_MS);
      } else {
        console.log('모든 문장 재생 완료');
        // 녹음이 비활성화된 경우 모든 문장 완료로 처리
        if (onResponse) {
          onResponse({
            hasStarted: true,
            currentSentenceIndex: index,
            sentenceRecordings: sentenceStates,
            isCorrect: true,
            isHomeworkReady: computeHomeworkReady({ sentenceIndex: index })
          });
        }
      }
    }
  };
  
  // 개별 문장 녹음 완료 핸들러
  const handleSentenceRecording = (sentenceId: string, sentenceIndex: number, audioBlob: Blob, audioUrl: string) => {
    console.log(`문장 ${sentenceId} 녹음 완료:`, audioBlob);
    
    const newState = {
      ...sentenceStates,
      [sentenceId]: { recorded: true, blob: audioBlob, url: audioUrl }
    };
    
    setSentenceStates(newState);
    
    // 모든 문장 녹음 완료 여부 확인
    const allRecorded = sentences.every(s => newState[s.id]?.recorded);
    
    if (onResponse) {
      if (isHomework) {
        // 숙제 모드: 매번 중간 진행 상태 저장
        console.log('숙제 모드 - 녹음 진행 상태 저장:', sentenceId);
        onResponse({
          hasStarted,
          currentSentenceIndex,
          sentenceRecordings: newState,
          isCorrect: true, // 숙제는 항상 완료로 처리
          isHomeworkReady: computeHomeworkReady({ recordings: newState })
        });
      } else {
        // 학습 모드: 모든 문장 녹음 완료 시에만 응답 저장
        if (allRecorded) {
          console.log('✅ 모든 문장 녹음 완료 - 다음 버튼 활성화');
          onResponse({
            hasStarted,
            currentSentenceIndex,
            sentenceRecordings: newState,
            isCorrect: true,
            isHomeworkReady: computeHomeworkReady({ recordings: newState })
          });
        } else {
          console.log(`녹음 진행 중: ${Object.keys(newState).length} / ${sentences.length}`);
        }
      }
    }
    
    // 다음 문장이 있으면 자동 재생
    const nextIndex = sentenceIndex + 1;
    if (nextIndex < sentences.length) {
      console.log(`다음 문장 ${nextIndex} 자동 재생 (지연 ${NEXT_AUDIO_DELAY_MS}ms)`);
      schedulePlayAudio(nextIndex, NEXT_AUDIO_DELAY_MS);
    } else {
      console.log('마지막 문장 녹음 완료');
    }
  };
  
  // 컴포넌트 마운트 시 초기 응답 저장
  useEffect(() => {
    if (existingResponse && existingResponse.sentenceRecordings && Object.keys(existingResponse.sentenceRecordings).length > 0) {
      console.log('StoryDialog: 기존 녹음 데이터 복원');
    } else if (onResponse) {
      // 초기 응답 저장
      console.log('StoryDialog: 초기 응답 저장');
      onResponse({
        hasStarted: false,
        currentSentenceIndex: -1,
        sentenceRecordings: {},
        isCorrect: false,
        isHomeworkReady: false
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      if (playDelayTimer.current) {
        clearTimeout(playDelayTimer.current);
      }
    };
  }, []);
  
  
  return (
    <main>
      <div>
        <h1 className='px-5 pt-5 pb-3 heading1'>{title}</h1>
        
        {/* 전체 스크립트 영역 */}
        <div className='px-5 pb-3'>
          <div className='relative flex flex-col items-center justify-center w-full gap-2 p-5 overflow-hidden bg-gray-50 border border-gray-200 rounded-2xl'>
            <p className='text-base text-left w-full pr-14 whitespace-pre-wrap'>{fullScript}</p>
            <div className='absolute bottom-3 right-3 flex items-center gap-2'>
              {!hasStarted ? (
                <button
                  onClick={handleStartListening}
                  className={`flex justify-center items-center p-1 rounded-full ${button}`}
                  style={{
                    width: 40,
                    height: 40,
                    background: 'linear-gradient(119deg, #ECEBFE 13.99%, #D8D6FF 86.01%)',
                  }}
                >
                  <div className='ml-1'>
                    <Icon name="listen" width='100%' height='100%' />
                  </div>
                </button>
              ) : (
                <div className='text-sm text-gray-500'>
                  진행 중: {Math.max(0, currentSentenceIndex + 1)} / {sentences.length}
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* 모든 오디오 엘리먼트를 항상 렌더링 (리렌더링 시에도 유지) */}
        <div style={{ display: 'none' }}>
          {sentences.map((sentence, index) => (
            <audio
              key={`audio-${sentence.id}`}
              ref={(el) => { audioRefs.current[index] = el }}
              src={sentence.audioUrl || ''}
              onEnded={() => handleAudioEnded(index)}
            />
          ))}
        </div>
        
        {/* 문장별 리스트 - 순차적으로 표시 */}
        <div className='px-5 space-y-3'>
          {sentences.map((sentence, index) => {
            // 현재 문장 이전까지만 표시
            const isVisible = index <= currentSentenceIndex;
            const isCurrentPlaying = isPlayingAudio && index === currentSentenceIndex + 1;
            
            if (!isVisible && !isCurrentPlaying) {
              return null;
            }
            
            return (
              <div 
                key={sentence.id} 
                className='border border-gray-200 rounded-xl p-4'
                style={{
                  backgroundColor: isCurrentPlaying ? '#FEF3C7' : (isVisible ? 'transparent' : '#F3F4F6'),
                  borderColor: isCurrentPlaying ? '#F59E0B' : '#E5E7EB',
                  opacity: isCurrentPlaying ? 0.7 : 1
                }}
              >
                <div className='flex items-start justify-between gap-3'>
                  <div className='flex-1'>
                    <div className='flex items-start gap-2'>
                      <span className='font-bold text-gray-700 flex-shrink-0'>{index + 1}.</span>
                      {isVisible ? (
                        <p className='text-base flex-1'>{sentence.text}</p>
                      ) : (
                        <p className='text-base flex-1 text-gray-400 italic'>재생 중...</p>
                      )}
                    </div>
                  </div>
                  
                  <div className='flex items-center gap-2 flex-shrink-0'>
                    {/* 녹음 완료 체크 */}
                    {sentenceStates[sentence.id]?.recorded && (
                      <div className='w-6 h-6 rounded-full bg-green-500 flex items-center justify-center'>
                        <svg className='w-4 h-4 text-white' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* 녹음 UI (문장이 보이고 recordingEnabled가 true일 때만 표시) */}
                {isVisible && recordingEnabled && (
                  <div className='mt-3'>
                    <RecordingBox 
                      onRecordingComplete={(blob, url) => handleSentenceRecording(sentence.id, index, blob, url)}
                      existingAudioUrl={sentenceStates[sentence.id]?.url || null}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </main>
  )
}